import { useEffect, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  LoadingSkeleton,
  Modal,
  Select,
  useToast,
} from '@nexora/design-system';
import { useBoms, type Bom } from './useBoms';
import {
  useBomRevisions,
  useCreateBomRevision,
  useApproveBomRevision,
  type BomRevision,
  type BomRevisionChange,
} from './useBomDetails';
import { formatDateTime } from '../../shared/utils/format';

/* ── Contrato (backend: bom-details.controller.ts + manufacturing-extended.dto.ts) ──
 *
 * GET  /manufacturing/boms/:id/revisions -> BomRevisionResponseDto[]
 * POST /manufacturing/boms/:id/revisions body: { version, changes[{ type, description, before?, after? }] }
 * POST /manufacturing/boms/:id/revisions/:revisionId/approve -> BomRevisionResponseDto
 *
 * BomRevisionResponseDto: { id, bomId, version, status (DRAFT|APPROVED|REJECTED|SUPERSEDED),
 *   changes[], approvedById?, approvedAt?, createdAt }
 *
 * Tela prototipo "BOM - Historico de Revisoes": timeline de versoes com status e mudancas.
 *
 * Fidelidade: o fluxo de aprovacao do prototipo (Engineering Review / Costing Validation /
 * Final Authorization) nao existe no contrato — a pagina mostra o status real da revisao
 * (DRAFT/APPROVED...) e o botao de aprovar apenas para DRAFT, que e a transicao que o
 * backend expoe.
 */

const REV_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'neutral' | 'error'> = {
  APPROVED: 'success',
  DRAFT: 'neutral',
  REJECTED: 'error',
  SUPERSEDED: 'warning',
};

const REV_STATUS_LABEL: Record<string, string> = {
  APPROVED: 'Aprovada',
  DRAFT: 'Rascunho',
  REJECTED: 'Rejeitada',
  SUPERSEDED: 'Substituída',
};

interface RevisionForm {
  version: string;
  changes: {
    type: string;
    description: string;
    before: string;
    after: string;
  }[];
}

const emptyForm: RevisionForm = { version: '', changes: [{ type: 'COMPONENT_ADDED', description: '', before: '', after: '' }] };

export function BomRevisionsPage(): JSX.Element {
  const { addToast } = useToast();
  const params = useParams({ strict: false }) as Record<string, string | undefined>;
  const routeId = params['bomId'] ?? params['id'];

  const [selectedId, setSelectedId] = useState<string>(routeId ?? '');
  useEffect(() => {
    if (routeId) setSelectedId(routeId);
  }, [routeId]);

  const { data: bomsData } = useBoms({ page: 1, limit: 100 });
  const boms = bomsData?.data ?? [];

  const { data: revisions, isLoading, error, refetch } = useBomRevisions(selectedId || undefined);
  const createRev = useCreateBomRevision();
  const approveRev = useApproveBomRevision();

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<RevisionForm>(emptyForm);
  const [approveTarget, setApproveTarget] = useState<BomRevision | null>(null);

  const openCreate = (): void => {
    setForm({ ...emptyForm, changes: [{ type: 'COMPONENT_ADDED', description: '', before: '', after: '' }] });
    setCreateOpen(true);
  };

  const handleCreate = async (): Promise<void> => {
    if (!selectedId) return;
    if (!form.version.trim()) {
      addToast('Informe a versão da revisão', 'error');
      return;
    }
    const changes: BomRevisionChange[] = form.changes
      .filter((c) => c.description.trim() !== '')
      .map((c) => ({
        type: c.type,
        description: c.description.trim(),
        before: c.before.trim() !== '' ? c.before.trim() : null,
        after: c.after.trim() !== '' ? c.after.trim() : null,
      }));
    if (changes.length === 0) {
      addToast('Adicione ao menos uma mudança com descrição', 'error');
      return;
    }
    try {
      await createRev.mutateAsync({ id: selectedId, version: form.version.trim(), changes });
      addToast('Revisão criada (rascunho)', 'success');
      setCreateOpen(false);
      setForm(emptyForm);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Erro ao criar revisão', 'error');
    }
  };

  const handleApprove = async (): Promise<void> => {
    if (!approveTarget || !selectedId) return;
    try {
      await approveRev.mutateAsync({ id: selectedId, revisionId: approveTarget.id });
      addToast('Revisão aprovada', 'success');
      setApproveTarget(null);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Erro ao aprovar revisão', 'error');
    }
  };

  return (
    <div className="bom-rev-page">
      <style>{`
        .bom-rev-page { padding: 1.5rem; }
        .bom-rev-empty { padding: 2rem 1rem; text-align: center; color: #43474e; }
        .bom-rev-error { padding: 1.5rem; text-align: center; color: #ba1a1a; }
        .bom-rev-timeline { position: relative; padding-left: 1.5rem; }
        .bom-rev-timeline::before { content: ''; position: absolute; left: 5px; top: 0; bottom: 0; width: 2px; background: #c4c6cf; }
        .bom-rev-item { position: relative; margin-bottom: 1.25rem; }
        .bom-rev-item::before { content: ''; position: absolute; left: -1.5rem; top: 4px; width: 10px; height: 10px; border-radius: 999px; background: #002045; border: 2px solid #ffffff; }
        .bom-rev-item--muted::before { background: #74777f; }
        .bom-rev-item--bad::before { background: #ba1a1a; }
        .bom-rev-change-box { background: #f2f4f6; border: 1px solid #e0e3e5; border-radius: 0.25rem; padding: 0.5rem 0.75rem; margin-top: 0.5rem; }
        .bom-rev-editor { display: flex; flex-direction: column; gap: 0.75rem; }
        .bom-rev-editor-row { display: grid; grid-template-columns: 1fr 2fr 1fr 1fr auto; gap: 0.5rem; align-items: end; }
        .bom-rev-workflow { display: flex; flex-direction: column; gap: 1rem; }
      `}</style>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <p className="font-label-sm text-label-sm text-on-surface-variant mb-xs uppercase tracking-wider">
            Produção / Estrutura de Produto (BOM)
          </p>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Revisões da Estrutura</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Histórico de versões e fluxo de aprovação da BOM.
          </p>
        </div>
        {selectedId && (
          <Button onClick={openCreate}>+ Nova Revisão</Button>
        )}
      </div>

      {!routeId && (
        <Card variant="outlined" padding="md" className="mb-lg">
          <div style={{ minWidth: '280px', maxWidth: '480px' }}>
            <Select
              label="Estrutura (BOM)"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              <option value="">— Selecionar —</option>
              {boms.map((b: Bom) => (
                <option key={b.id} value={b.id}>
                  {b.description} (v{b.version})
                </option>
              ))}
            </Select>
          </div>
        </Card>
      )}

      {isLoading && (
        <Card padding="1rem">
          <LoadingSkeleton count={6} height="48px" />
        </Card>
      )}

      {error && !isLoading && (
        <Card padding="2rem">
          <div className="bom-rev-error" role="alert">
            <p>Erro ao carregar as revisões</p>
            <p className="font-body-sm text-body-sm text-on-surface-variant">{(error as Error).message}</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>Tentar novamente</Button>
          </div>
        </Card>
      )}

      {!isLoading && !error && !selectedId && (
        <Card padding="2rem">
          <div className="bom-rev-empty">Nenhuma estrutura selecionada.</div>
        </Card>
      )}

      {!isLoading && !error && selectedId && (revisions ?? []).length === 0 && (
        <Card padding="2rem">
          <div className="bom-rev-empty">
            Nenhuma revisão registrada para esta estrutura. Clique em “+ Nova Revisão” para criar a
            primeira versão (rascunho).
          </div>
        </Card>
      )}

      {!isLoading && !error && selectedId && (revisions ?? []).length > 0 && (
        <Card padding="md">
          <CardHeader className="pb-sm">
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">
              Revision History
            </h3>
          </CardHeader>
          <div className="bom-rev-timeline">
            {(revisions ?? []).map((rev) => {
              const variant = REV_STATUS_VARIANT[rev.status] ?? 'neutral';
              return (
                <div
                  key={rev.id}
                  className={`bom-rev-item ${rev.status === 'APPROVED' ? '' : rev.status === 'REJECTED' ? 'bom-rev-item--bad' : rev.status === 'DRAFT' ? 'bom-rev-item--muted' : ''}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-sm">
                    <div className="font-headline-sm text-headline-sm text-on-surface">
                      Version {rev.version}
                      <span className="font-label-md text-label-md text-on-surface-variant ml-sm">
                        {formatDateTime(rev.createdAt)}
                      </span>
                    </div>
                    <div className="flex items-center gap-sm">
                      <Badge variant={variant} size="sm">{REV_STATUS_LABEL[rev.status] ?? rev.status}</Badge>
                      {rev.status === 'DRAFT' && (
                        <Button size="sm" variant="primary" onClick={() => setApproveTarget(rev)}>
                          Aprovar
                        </Button>
                      )}
                    </div>
                  </div>
                  {rev.approvedAt && (
                    <p className="font-label-sm text-label-sm text-on-surface-variant mt-xs m-0">
                      Aprovada em {formatDateTime(rev.approvedAt)}
                      {rev.approvedById ? ` · por ${rev.approvedById.slice(0, 8)}` : ''}
                    </p>
                  )}
                  {rev.changes.length > 0 && (
                    <div className="flex flex-col gap-xs mt-sm">
                      {rev.changes.map((ch, idx) => (
                        <div key={idx} className="bom-rev-change-box">
                          <div className="flex items-center gap-sm flex-wrap">
                            <Badge variant="neutral" size="sm">{ch.type}</Badge>
                            {ch.before != null && ch.after != null && ch.before !== ch.after && (
                              <span className="font-body-sm text-body-sm">
                                <span className="text-error" style={{ textDecoration: 'line-through' }}>
                                  {String(ch.before)}
                                </span>
                                <span className="text-on-surface-variant"> → </span>
                                <span className="text-primary font-semibold">{String(ch.after)}</span>
                              </span>
                            )}
                          </div>
                          <p className="font-body-sm text-body-sm text-on-surface m-0 mt-xs">{ch.description}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Nova revisao */}
      <Modal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Nova Revisão"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={() => void handleCreate()} loading={createRev.isPending}>Criar revisão</Button>
          </>
        }
      >
        <div className="bom-rev-editor">
          <Input
            label="Versão *"
            placeholder="Ex: 2.4"
            value={form.version}
            onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))}
          />
          <p className="font-label-md text-label-md text-on-surface-variant uppercase m-0">
            Mudanças desta revisão
          </p>
          {form.changes.map((ch, idx) => (
            <div key={idx} className="bom-rev-editor-row">
              <Select
                label="Tipo"
                value={ch.type}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    changes: f.changes.map((c, i) => (i === idx ? { ...c, type: e.target.value } : c)),
                  }))
                }
              >
                <option value="COMPONENT_ADDED">Componente adicionado</option>
                <option value="COMPONENT_REMOVED">Componente removido</option>
                <option value="QUANTITY_CHANGED">Quantidade alterada</option>
                <option value="UNIT_COST_CHANGED">Custo unitário alterado</option>
                <option value="SETUP_TIME_CHANGED">Tempo de setup alterado</option>
                <option value="LABOR_CHANGED">Mão de obra alterada</option>
                <option value="OTHER">Outro</option>
              </Select>
              <Input
                label="Descrição *"
                placeholder="Ex: Adicionado componente X"
                value={ch.description}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    changes: f.changes.map((c, i) => (i === idx ? { ...c, description: e.target.value } : c)),
                  }))
                }
              />
              <Input
                label="De"
                placeholder="Antes"
                value={ch.before}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    changes: f.changes.map((c, i) => (i === idx ? { ...c, before: e.target.value } : c)),
                  }))
                }
              />
              <Input
                label="Para"
                placeholder="Depois"
                value={ch.after}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    changes: f.changes.map((c, i) => (i === idx ? { ...c, after: e.target.value } : c)),
                  }))
                }
              />
              <Button
                size="sm"
                variant="danger"
                onClick={() => setForm((f) => ({ ...f, changes: f.changes.filter((_, i) => i !== idx) }))}
              >
                Remover
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setForm((f) => ({
                ...f,
                changes: [
                  ...f.changes,
                  { type: 'COMPONENT_ADDED', description: '', before: '', after: '' },
                ],
              }))
            }
          >
            + Adicionar mudança
          </Button>
        </div>
      </Modal>

      {/* Aprovar revisao (workflow) */}
      <Modal
        isOpen={!!approveTarget}
        onClose={() => setApproveTarget(null)}
        title="Aprovar revisão"
        footer={
          <>
            <Button variant="ghost" onClick={() => setApproveTarget(null)}>Cancelar</Button>
            <Button onClick={() => void handleApprove()} loading={approveRev.isPending}>Aprovar</Button>
          </>
        }
      >
        <p className="font-body-md text-body-md text-on-surface m-0">
          Aprovar a versão <strong>{approveTarget?.version}</strong> desta estrutura? A
          mudança de status é registrada pelo backend com o usuário logado.
        </p>
      </Modal>
    </div>
  );
}